import { Analytics } from '@cobliteam/analytics';
import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { formatVehicleLabel } from '@cobliteam/ui-business/components/Selects/SelectVehicle/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';
import { rowsForRetry, runSequentialSaves } from './sequentialSubmit';

/** Rows shown in the modal; only the first pair is required. */
const ROWS = 2;

/**
 * Minimal "associate drivers and vehicles" modal — rows of (driver × vehicle without driver), no frame
 * of its own in the Figma: segue o mesmo critério da associação veículo↔dispositivo (node 148:10415),
 * trocando o dispositivo pelo motorista. O motorista é escolhido pelo nome e o veículo pela placa.
 *
 * A associação é o `driverId` do veículo — o mesmo campo que alimenta a atribuição de viagens por
 * condutor e a contagem de maturidade — gravado via `updateVehicle`, como no passo inline que esta
 * modal substitui.
 */
const DriverAssociationForm = ({ fleetId, item, onClose, onSuccess }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { data: vehicles } = useVehicle().useVehicles({});
  const { data: drivers } = useDriver().useDrivers();
  const { mutateAsync: updateVehicle, isPending } = useFuscaVehicle().updateVehicle;

  const availableVehicles = useMemo(() => (vehicles ?? []).filter((vehicle) => !vehicle.driverId), [vehicles]);
  // Só motoristas sem veículo atribuído: a associação vive no `driverId` do veículo, então oferecer um
  // motorista já vinculado deixaria o mesmo condutor em dois veículos. Simétrico ao filtro de veículos
  // abaixo, e ao que o DeviceAssociationForm faz nos dois lados.
  const sortedDrivers = useMemo(
    () =>
      (drivers ?? [])
        .filter((driver) => !driver.vehicle)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [drivers]
  );
  // No point rendering more rows than there are pairs left to associate.
  const rowCount = Math.max(1, Math.min(ROWS, availableVehicles.length, sortedDrivers.length));
  const hasPairs = availableVehicles.length > 0 && sortedDrivers.length > 0;

  const schema = useMemo(
    () =>
      z.object({
        rows: z.array(z.object({ driverId: z.string(), vehicleId: z.string() })).superRefine((rows, ctx) => {
          rows.forEach((row, index) => {
            if (!(row.driverId || row.vehicleId)) {
              // Only the first row is mandatory; later rows may stay empty.
              if (index === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'driverId'], message: t('entity_modal.driver-association.validation.pair_required') });
              return;
            }
            if (!row.driverId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'driverId'], message: t('entity_modal.driver-association.validation.pair_required') });
            if (!row.vehicleId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'vehicleId'], message: t('entity_modal.driver-association.validation.pair_required') });
          });
        }),
      }),
    [t]
  );

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    // Always register every possible row (rowCount only limits rendering): the queries resolve after
    // mount, and a row that appears later without a registered default would flip its Selects from
    // uncontrolled to controlled.
    defaultValues: { rows: Array.from({ length: ROWS }, () => ({ driverId: '', vehicleId: '' })) },
  });
  const selectedRows = useWatch({ control: form.control, name: 'rows' });

  const handleSubmit = async ({ rows }: FormValues) => {
    // Original indexes travel along so a partial failure can clear exactly the rows that saved.
    const filled = rows.map((row, rowIndex) => ({ row, rowIndex })).filter(({ row }) => row.driverId && row.vehicleId);
    // Sequential saves; a failed row doesn't stop the following ones and the fusca hook toasts failures.
    const { succeeded, failed } = await runSequentialSaves(filled, ({ row }) => {
      const vehicle = availableVehicles.find((candidate) => candidate.id === row.vehicleId);
      const driver = sortedDrivers.find((candidate) => candidate.id === row.driverId);
      if (!(vehicle && driver)) return Promise.reject(new Error('Par de motorista e veículo indisponível'));
      return updateVehicle({ ...vehicle, newDriver: { id: driver.id, name: driver.name, phoneNumbers: driver.phoneNumbers ?? [] } });
    });
    // Associated pairs exist even when another row fails, so the event reflects reality.
    if (succeeded.length > 0) Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'driver-association', count: succeeded.length });
    if (failed.length > 0) {
      // Keep the form open for retry, dropping the rows that saved so retrying can't re-associate them.
      form.reset({ rows: rowsForRetry(rows, new Set(succeeded.map(({ rowIndex }) => rowIndex)), ROWS, () => ({ driverId: '', vehicleId: '' })) });
      void form.trigger();
      return;
    }
    onSuccess(succeeded.length);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.driver-association.title')}
      description={t('entity_modal.driver-association.description')}
      primaryAction={{
        label: t('entity_modal.driver-association.submit'),
        onClick: form.handleSubmit(handleSubmit),
        disabled: !(hasPairs && form.formState.isValid) || isPending,
        loading: isPending,
        endIcon: <Icon name='check' size='sm' />,
        'data-testid': 'setup-guide-driver-association-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.driver-association.progress', { current: item.current, target: item.target })} />
          {hasPairs ? (
            Array.from({ length: rowCount }, (_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size row list, never reordered
              <SetupGuideFormRow key={index} label={t('entity_modal.driver-association.row_label', { index: index + 1 })}>
                <FormField control={form.control} name={`rows.${index}.driverId`}>
                  {({ field }) => (
                    <FormItem className='w-64'>
                      <FormLabel>{t('entity_modal.driver-association.driver_label')}</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('entity_modal.driver-association.select_placeholder')} />
                          </SelectTrigger>
                          <SelectContent portal={false}>
                            {sortedDrivers.map((driver) => (
                              <SelectItem key={driver.id} value={driver.id} disabled={selectedRows.some((row, rowIndex) => rowIndex !== index && row.driverId === driver.id)}>
                                {driver.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </FormField>
                <FormField control={form.control} name={`rows.${index}.vehicleId`}>
                  {({ field }) => (
                    <FormItem className='w-64'>
                      <FormLabel>{t('entity_modal.driver-association.vehicle_label')}</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('entity_modal.driver-association.select_placeholder')} />
                          </SelectTrigger>
                          <SelectContent portal={false}>
                            {availableVehicles.map((vehicle) => (
                              <SelectItem key={vehicle.id} value={vehicle.id} disabled={selectedRows.some((row, rowIndex) => rowIndex !== index && row.vehicleId === vehicle.id)}>
                                {formatVehicleLabel(vehicle)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                </FormField>
              </SetupGuideFormRow>
            ))
          ) : (
            <Typography variant='text-md' className='py-8 text-center text-gray-11'>
              {t('entity_modal.driver-association.empty')}
            </Typography>
          )}
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default DriverAssociationForm;
